import React, { useState } from 'react';
import { GUITAR_CHORDS, GUITAR_STRINGS, ChordData, getChordFrequencies } from '../../utils/guitarTheory';
import { soundEngine } from '../../utils/audio';
import { Sparkles, Music, Volume2 } from 'lucide-react';
import confetti from 'canvas-confetti';

export const ChordStrummer: React.FC = () => {
  const [selectedChord, setSelectedChord] = useState<ChordData>(GUITAR_CHORDS[0]);
  const [vibratingString, setVibratingString] = useState<number | null>(null);
  const [isStrumming, setIsStrumming] = useState(false);

  // Pluck a single string in the current chord
  const handlePluckString = (stringIndex: number) => {
    // stringIndex: 0 (High E) to 5 (Low E)
    const fret = selectedChord.frets[stringIndex];
    if (fret === -1) {
      soundEngine.playIncorrectSfx();
      return;
    }
    const str = GUITAR_STRINGS[stringIndex];
    const freq = str.baseFreq * Math.pow(2, fret / 12);
    
    setVibratingString(stringIndex);
    soundEngine.playGuitarPluck(freq, 2.4, 0.55);
    setTimeout(() => setVibratingString(null), 500);
  };

  // Strum all active strings
  const handleStrum = () => {
    setIsStrumming(true);
    soundEngine.playPopSfx();
    const freqs = getChordFrequencies(selectedChord);
    soundEngine.strumChord(freqs, 40);

    // Trigger sweet mini confetti if it's a favorite chord
    confetti({
      particleCount: 25,
      spread: 60,
      origin: { y: 0.7 },
      colors: ['#F472B6', '#C084FC', '#FDE047']
    });

    setTimeout(() => setIsStrumming(false), 600);
  };

  return (
    <div className="space-y-8">
      
      {/* Chord Selection Carousel / Grid */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-bold text-gray-700 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-pink-500" />
            <span>Select a Guitar Chord to Learn:</span>
          </label>
          <span className="text-xs text-purple-600 font-semibold bg-purple-50 px-2.5 py-1 rounded-full">
            {GUITAR_CHORDS.length} Chords in Library
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 md:grid-cols-5 gap-2.5">
          {GUITAR_CHORDS.map((chord) => {
            const isSelected = selectedChord.id === chord.id;
            return (
              <button
                key={chord.id}
                onClick={() => {
                  soundEngine.playPopSfx();
                  setSelectedChord(chord);
                }}
                className={`p-3 rounded-2xl border-2 text-center transition-all cursor-pointer relative ${
                  isSelected
                    ? 'border-pink-500 bg-gradient-to-b from-pink-50 to-purple-50 ring-2 ring-pink-200 shadow-sm scale-102 font-bold'
                    : 'border-pink-100 bg-white hover:border-pink-300 hover:bg-pink-50/30'
                }`}
              >
                <div className="text-xl sm:text-2xl font-extrabold text-purple-900">{chord.symbol}</div>
                <div className="text-xs text-pink-600 font-medium truncate mt-0.5">{chord.name}</div>
                <div className="text-[10px] text-gray-400 mt-1">{chord.difficulty}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Interactive Chord Display & Strumming Stage */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left: Interactive Guitar Chord Chart (Interactive fretboard box) */}
        <div className="lg:col-span-7 bg-white rounded-3xl p-6 sm:p-8 border-2 border-pink-200 shadow-md">
          
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="px-3 py-1 rounded-full bg-pink-100 text-pink-700 font-bold text-xs uppercase">
                {selectedChord.category} Chord
              </span>
              <h3 className="text-3xl font-extrabold text-gray-800 mt-1">
                {selectedChord.name} ({selectedChord.symbol})
              </h3>
            </div>
            
            <button
              onClick={handleStrum}
              disabled={isStrumming}
              className={`px-6 py-3 rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold text-sm shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all flex items-center gap-2 cursor-pointer ${
                isStrumming ? 'animate-bounce' : ''
              }`}
            >
              <Music className="w-4 h-4" />
              <span>Strum Chord! 🎶</span>
            </button>
          </div>

          <p className="text-xs text-gray-500 mb-6 flex items-center gap-1.5">
            <Volume2 className="w-3.5 h-3.5 text-pink-500" />
            <span>Click any individual string below to pluck it, or hit <b>Strum Chord</b> to hear the whole harmony!</span>
          </p>

          {/* Graphical Chord Box */}
          <div className="max-w-sm mx-auto bg-amber-50/70 border-4 border-amber-800/60 rounded-2xl p-5 shadow-inner">
            
            {/* Top Nut / Mute-Open String Indicators */}
            {/* We map from String 6 (Low E) down to String 1 (High E) left-to-right like traditional chord charts */}
            <div className="grid grid-cols-6 text-center mb-2">
              {[5, 4, 3, 2, 1, 0].map((strIdx) => {
                const fret = selectedChord.frets[strIdx];
                const isMute = fret === -1;
                const isOpen = fret === 0;
                return (
                  <div key={strIdx} className="font-extrabold text-sm sm:text-base">
                    {isMute ? (
                      <span className="text-red-500">✕</span>
                    ) : isOpen ? (
                      <span className="text-emerald-500">○</span>
                    ) : (
                      <span className="text-purple-400 text-xs">fret {fret}</span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Nut (thick line representing top of guitar neck) */}
            <div className="h-2 bg-amber-900 rounded-sm mb-1" />

            {/* Frets Grid (Frets 1, 2, 3, 4) */}
            <div className="relative border-x-2 border-b-2 border-amber-900/60 bg-amber-100/40 divide-y-2 divide-amber-900/50">
              
              {[1, 2, 3, 4].map((fretNum) => (
                <div key={fretNum} className="relative h-14 sm:h-16 flex items-center">
                  
                  {/* Fret label on side */}
                  <span className="absolute -left-7 text-[11px] font-bold text-amber-900/70">
                    F{fretNum}
                  </span>

                  {/* 6 Vertical Strings rendered inside each fret row */}
                  <div className="w-full grid grid-cols-6 h-full relative">
                    {[5, 4, 3, 2, 1, 0].map((strIdx) => {
                      const chordFret = selectedChord.frets[strIdx];
                      const finger = selectedChord.fingers[strIdx];
                      const hasFingerOnThisFret = chordFret === fretNum;
                      const isVibrating = vibratingString === strIdx;

                      return (
                        <div
                          key={strIdx}
                          onClick={() => handlePluckString(strIdx)}
                          className="relative h-full flex items-center justify-center cursor-pointer group"
                        >
                          {/* Vertical String line (thicker for lower strings) */}
                          <div
                            className={`absolute top-0 bottom-0 transition-transform ${
                              isVibrating ? 'string-vibrating bg-pink-500' : 'bg-amber-800/80 group-hover:bg-pink-400'
                            }`}
                            style={{
                              width: `${Math.max(1.5, 4 - strIdx * 0.5)}px`,
                            }}
                          />

                          {/* Finger placement dot */}
                          {hasFingerOnThisFret && (
                            <div className="relative z-10 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-tr from-pink-500 to-rose-400 text-white font-extrabold text-xs sm:text-sm flex items-center justify-center shadow-md animate-scaleIn border-2 border-white">
                              {finger}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom String Names: 6th (E), 5th (A), 4th (D), 3rd (G), 2nd (B), 1st (e) */}
            <div className="grid grid-cols-6 text-center mt-3">
              {[5, 4, 3, 2, 1, 0].map((strIdx) => {
                const str = GUITAR_STRINGS[strIdx];
                return (
                  <button
                    key={strIdx}
                    onClick={() => handlePluckString(strIdx)}
                    className="p-1 rounded-lg hover:bg-pink-100 transition-colors cursor-pointer"
                    title={`Click to pluck ${str.name}`}
                  >
                    <div className="text-xs font-bold text-gray-700">{str.baseNote}</div>
                    <div className="text-[10px] text-gray-400">{strIdx === 0 ? 'High' : strIdx === 5 ? 'Low' : ''}</div>
                  </button>
                );
              })}
            </div>

          </div>

          {/* Finger legend */}
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3 text-xs text-gray-600 bg-pink-50/50 p-2.5 rounded-xl border border-pink-100">
            <span className="font-bold text-purple-900">Finger Legend:</span>
            <span>① Index</span>
            <span>② Middle</span>
            <span>③ Ring</span>
            <span>④ Pinky</span>
          </div>

        </div>

        {/* Right: Cute Chord Breakdown & Melodalis Tips */}
        <div className="lg:col-span-5 space-y-4">
          
          {/* Kawaii Vibe Card */}
          <div className="rounded-3xl p-6 bg-gradient-to-br from-pink-100/90 via-purple-100/80 to-rose-100/90 border-2 border-pink-200 shadow-sm space-y-3">
            <div className="flex items-center gap-2 text-pink-700 font-bold text-sm">
              <Sparkles className="w-4 h-4" />
              <span>Kawaii Chord Mood</span>
            </div>
            <div className="text-xl font-bold text-purple-950">
              {selectedChord.kawaiiVibe}
            </div>
            <p className="text-sm text-purple-900/80">
              {selectedChord.description}
            </p>
          </div>



          {/* Interactive Strum Prompt */}
          <div className="rounded-3xl p-5 bg-gradient-to-r from-yellow-100 to-amber-100 border-2 border-yellow-200 text-amber-900 flex items-center gap-4">
            <div className="text-3xl">🎸</div>
            <div className="text-xs">
              <div className="font-bold text-sm mb-0.5">Did you know?</div>
              <span>Most hit songs on the radio use just 4 chords: <b>C, G, Am, and F</b>! Try playing them in order!</span>
            </div>
          </div>

        </div>

      </div>

    </div>
  );
};

